import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove } from "@dnd-kit/sortable";
import { motion } from "framer-motion";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSpinDelay } from 'spin-delay';
import { v4 as uuidv4 } from 'uuid';
import { Alert } from "../Alert";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Spinner } from "../Spinner";
import { cn } from "../utils/tailwind";
import { matchesExactKey, createPlatformShortcut } from "../utils/keyboardUtils";
import { useOperationHistory } from "../utils/useOperationHistory";
import { useFilterOperators, } from "./common";
import { FilterCondition } from "./FilterCondition";
import { FilterGroup } from "./FilterGroup";
import styles from './FilterPanel.module.css';
import { useLocalizedStrings } from "../LocalizationProvider";
function removeItemById(group, id) {
    let removed = null;
    const clauses = group.clauses.reduce((acc, clause) => {
        if (clause.id === id) {
            removed = clause;
            return acc;
        }
        if (!removed && 'clauses' in clause) {
            const result = removeItemById(clause, id);
            if (result.item) {
                removed = result.item;
                acc.push(result.group);
                return acc;
            }
        }
        acc.push(clause);
        return acc;
    }, []);
    return { item: removed, group: { ...group, clauses } };
}
function addItemToGroup(group, groupId, item) {
    if (group.id === groupId) {
        return { ...group, clauses: [...group.clauses, item] };
    }
    return {
        ...group,
        clauses: group.clauses.map(clause => 'clauses' in clause
            ? addItemToGroup(clause, groupId, item)
            : clause)
    };
}
function insertItemBefore(group, targetId, item) {
    const clauses = group.clauses.reduce((acc, clause) => {
        if (clause.id === targetId) {
            acc.push(item, clause);
        }
        else if ('clauses' in clause) {
            acc.push(insertItemBefore(clause, targetId, item));
        }
        else {
            acc.push(clause);
        }
        return acc;
    }, []);
    return { ...group, clauses };
}
function isGroupById(group, id) {
    if (group.id === id)
        return true;
    return group.clauses.some(clause => 'clauses' in clause && isGroupById(clause, id));
}
function reorderItems(group, activeId, overId) {
    const findItemLocation = (g, itemId, path = []) => {
        const index = g.clauses.findIndex(clause => clause.id === itemId);
        if (index !== -1) {
            return { group: g, index, path };
        }
        for (let i = 0; i < g.clauses.length; i++) {
            const clause = g.clauses[i];
            if ('clauses' in clause) {
                const result = findItemLocation(clause, itemId, [...path, i]);
                if (result)
                    return result;
            }
        }
        return null;
    };
    const activeLocation = findItemLocation(group, activeId);
    const overLocation = findItemLocation(group, overId);
    if (!activeLocation || !overLocation)
        return null;
    if (activeLocation.group.id === overLocation.group.id) {
        const newClauses = arrayMove(activeLocation.group.clauses, activeLocation.index, overLocation.index);
        return updateGroupAtPath(group, activeLocation.path, { clauses: newClauses });
    }
    return null;
}
function updateGroupAtPath(group, path, updates) {
    if (path.length === 0) {
        return { ...group, ...updates };
    }
    const [index, ...restPath] = path;
    const clause = group.clauses[index];
    if ('clauses' in clause) {
        const updatedClause = updateGroupAtPath(clause, restPath, updates);
        const newClauses = [...group.clauses];
        newClauses[index] = updatedClause;
        return { ...group, clauses: newClauses };
    }
    return group;
}
function cleanupRedundantNesting(group) {
    const cleanedClauses = group.clauses.map(clause => {
        if ('clauses' in clause) {
            const cleanedNestedGroup = cleanupRedundantNesting(clause);
            if (cleanedNestedGroup.clauses.length === 1 && 'clauses' in cleanedNestedGroup.clauses[0]) {
                const onlyChild = cleanedNestedGroup.clauses[0];
                return {
                    ...onlyChild,
                    id: cleanedNestedGroup.id
                };
            }
            return cleanedNestedGroup;
        }
        return clause;
    });
    return {
        ...group,
        clauses: cleanedClauses
    };
}
function convertToReportConfig(group) {
    return {
        logic: group.kind.toLowerCase(),
        conditions: group.clauses.map(clause => {
            if ('field' in clause) {
                const { id, ...cond } = clause;
                return cond;
            }
            return convertToReportConfig(clause);
        })
    };
}
function countActiveFilters(group) {
    let count = 0;
    for (const clause of group.clauses) {
        if ('field' in clause) {
            count++;
        }
        else {
            count += countActiveFilters(clause);
        }
    }
    return count;
}
const adjustOverlayPosition = ({ transform }) => {
    return {
        ...transform,
        x: transform.x - 200,
        y: transform.y - 150,
    };
};
export const FilterPanel = forwardRef(({ value, onChange, availableFields = [], className, maxDepth = 3, onConfirm, instanceId, isReloading = false, onOpenChange }, ref) => {
    const L = useLocalizedStrings();
    const { OPERATORS_BY_TYPE } = useFilterOperators();
    const [isOpen, setIsOpen] = useState(false);
    const filterPanelRef = useRef(null);
    const [showInteractionWarning, setShowInteractionWarning] = useState(false);
    const [alertKey, setAlertKey] = useState(0);
    const [isFlashing, setIsFlashing] = useState(false);
    const addConditionButtonRef = useRef(null);
    const initialTree = React.useMemo(() => {
        if (value?.config) {
            function assignIds(config) {
                const kind = config.logic === 'and' ? 'And' : 'Or';
                return {
                    id: uuidv4(),
                    kind,
                    clauses: config.conditions?.map(clause => 'field' in clause
                        ? ({ id: uuidv4(), ...clause })
                        : assignIds(clause)) || []
                };
            }
            return assignIds(value.config);
        }
        return { id: uuidv4(), kind: 'And', clauses: [] };
    }, [value?.config]);
    const sessionKey = `reports-filter-${instanceId ?? 'default'}`;
    const operationsHistory = useOperationHistory(sessionKey, initialTree, 20, 1000);
    useEffect(() => {
        if (instanceId === undefined || instanceId === null) {
            sessionStorage.removeItem(sessionKey);
        }
        return () => {
            if (minorChangeTimeoutRef.current) {
                clearTimeout(minorChangeTimeoutRef.current);
                minorChangeTimeoutRef.current = null;
                if (pendingMinorChangeRef.current) {
                    operationsHistory.push(pendingMinorChangeRef.current);
                    pendingMinorChangeRef.current = null;
                }
            }
            sessionStorage.removeItem(sessionKey);
        };
    }, [sessionKey, instanceId]);
    const minorChangeTimeoutRef = useRef(null);
    const pendingMinorChangeRef = useRef(null);
    const [currentTree, setCurrentTree] = useState(operationsHistory.current);
    useEffect(() => {
        setCurrentTree(operationsHistory.current);
    }, [operationsHistory.current]);
    const tree = currentTree;
    const commitPendingMinorChange = useCallback(() => {
        if (minorChangeTimeoutRef.current) {
            clearTimeout(minorChangeTimeoutRef.current);
            minorChangeTimeoutRef.current = null;
        }
        if (pendingMinorChangeRef.current) {
            operationsHistory.push(pendingMinorChangeRef.current);
            pendingMinorChangeRef.current = null;
        }
    }, [operationsHistory]);
    const [initialConfig, setInitialConfig] = useState(() => value || { enabled: true, config: { logic: 'and', conditions: [] } });
    useEffect(() => {
        setInitialConfig(value || { enabled: true, config: { logic: 'and', conditions: [] } });
    }, [value]);
    const hasUnsavedChanges = onConfirm && JSON.stringify(convertToReportConfig(tree)) !== JSON.stringify(initialConfig.config);
    const memoizedCleanup = React.useMemo(() => cleanupRedundantNesting, []);
    const emitChange = useCallback((newTree, addToHistory = true) => {
        const cleanedTree = memoizedCleanup(newTree);
        setCurrentTree(cleanedTree);
        if (addToHistory) {
            operationsHistory.push(cleanedTree);
        }
        if (cleanedTree.clauses.length === 0) {
            onChange?.({ enabled: false });
        }
        else {
            onChange?.({ enabled: true, config: convertToReportConfig(cleanedTree) });
        }
    }, [operationsHistory, onChange, memoizedCleanup]);
    const emitMajorChange = useCallback((newTree) => {
        if (minorChangeTimeoutRef.current) {
            clearTimeout(minorChangeTimeoutRef.current);
            commitPendingMinorChange();
        }
        emitChange(newTree, true);
    }, [emitChange, commitPendingMinorChange]);
    const emitMinorChange = useCallback((newTree) => {
        emitChange(newTree, false);
        pendingMinorChangeRef.current = newTree;
        if (minorChangeTimeoutRef.current) {
            clearTimeout(minorChangeTimeoutRef.current);
            minorChangeTimeoutRef.current = null;
        }
        minorChangeTimeoutRef.current = setTimeout(() => {
            if (pendingMinorChangeRef.current) {
                operationsHistory.push(pendingMinorChangeRef.current);
                pendingMinorChangeRef.current = null;
            }
            minorChangeTimeoutRef.current = null;
        }, 1000);
    }, [emitChange, operationsHistory]);
    const findLastCondition = (group) => {
        for (let i = group.clauses.length - 1; i >= 0; i--) {
            const clause = group.clauses[i];
            if ('field' in clause) {
                return clause;
            }
            else {
                const found = findLastCondition(clause);
                if (found)
                    return found;
            }
        }
        return null;
    };
    const handleAddCondition = (shouldFocusNewCondition = false) => {
        const lastCondition = findLastCondition(tree);
        const defaultFieldKey = lastCondition?.field || availableFields[0]?.key || '';
        const defaultField = availableFields.find(field => field.key === defaultFieldKey) || availableFields[0];
        const defaultOperator = defaultField ? OPERATORS_BY_TYPE[defaultField.type]?.[0] : null;
        const newCondId = uuidv4();
        const newCond = {
            id: newCondId,
            field: defaultField?.key || "",
            operator: defaultOperator?.value || 'equals',
            value: defaultOperator?.requiresValue ? "" : undefined
        };
        emitMajorChange(addItemToGroup(tree, tree.id, newCond));
        if (shouldFocusNewCondition) {
            requestAnimationFrame(() => {
                if (filterPanelRef.current) {
                    const newConditionSelect = filterPanelRef.current.querySelector(`[data-condition-id="${newCondId}"] [role="combobox"]`);
                    if (newConditionSelect) {
                        newConditionSelect.focus();
                    }
                }
            });
        }
    };
    const handleAddGroup = () => {
        const newGroup = {
            id: uuidv4(),
            kind: 'And',
            clauses: []
        };
        emitMajorChange(addItemToGroup(tree, tree.id, newGroup));
    };
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, {
        onActivation: () => setIsKeyboardDrag(false)
    }), useSensor(KeyboardSensor, {
        onActivation: () => setIsKeyboardDrag(true)
    }));
    const triggerFlash = useCallback(() => {
        setIsFlashing(false);
        requestAnimationFrame(() => {
            setIsFlashing(true);
            setTimeout(() => setIsFlashing(false), 1200);
        });
    }, []);
    const handleUndo = useCallback(() => {
        if (operationsHistory.canUndo) {
            const newTree = operationsHistory.undo();
            triggerFlash();
            if (newTree) {
                emitChange(newTree, false);
            }
        }
    }, [operationsHistory, triggerFlash, emitChange]);
    const handleRedo = useCallback(() => {
        if (operationsHistory.canRedo) {
            const newTree = operationsHistory.redo();
            triggerFlash();
            if (newTree) {
                emitChange(newTree, false);
            }
        }
    }, [operationsHistory, triggerFlash, emitChange]);
    const [isKeyboardDrag, setIsKeyboardDrag] = useState(false);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleKeyDown = (event) => {
            if (matchesExactKey(event, createPlatformShortcut('z'))) {
                event.preventDefault();
                handleUndo();
            }
            if (matchesExactKey(event, createPlatformShortcut('z', { shift: true }))) {
                event.preventDefault();
                handleRedo();
            }
            if (matchesExactKey(event, createPlatformShortcut('y'))) {
                event.preventDefault();
                handleRedo();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleUndo, handleRedo]);
    const handleEscapeKeyDown = (event) => {
        if (activeId) {
            event.preventDefault();
        }
    };
    const customCollisionDetection = (args) => {
        if (isKeyboardDrag) {
            return closestCenter(args);
        }
        else {
            const pointerCollisions = pointerWithin(args);
            if (pointerCollisions.length === 0) {
                return closestCenter(args);
            }
            return pointerCollisions;
        }
    };
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragOver = (event) => {
        const overId = event.over?.id || null;
        setOverId(overId);
    };
    const handleDragCancel = () => {
        setActiveId(null);
        setOverId(null);
        setIsKeyboardDrag(false);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setOverId(null);
        setIsKeyboardDrag(false);
        if (!over) {
            return;
        }
        const activeId = active.id;
        const overId = over.id;
        if (active.id === over.id) {
            return;
        }
        const isItemToItemMove = !overId.includes('-') && !isGroupById(tree, overId);
        if (isItemToItemMove) {
            const reorderResult = reorderItems(tree, activeId, overId);
            if (reorderResult) {
                emitMajorChange(reorderResult);
                return;
            }
            else {
                return;
            }
        }
        else {
        }
        const { item, group: groupWithoutItem } = removeItemById(tree, activeId);
        if (!item) {
            return;
        }
        let updatedTree = groupWithoutItem;
        let inserted = false;
        if (overId.includes('-before-')) {
            const targetId = overId.split('-before-')[1];
            if (targetId === activeId) {
                emitChange(tree, false);
                return;
            }
            updatedTree = insertItemBefore(updatedTree, targetId, item);
            inserted = true;
        }
        else if (overId.includes('-after-')) {
            const targetId = overId.split('-after-')[1];
            if (targetId === activeId) {
                emitChange(tree, false);
                return;
            }
            const findAndInsertAfter = (grp) => {
                const targetIndex = grp.clauses.findIndex(c => c.id === targetId);
                if (targetIndex !== -1) {
                    const newClauses = [...grp.clauses];
                    newClauses.splice(targetIndex + 1, 0, item);
                    return { group: { ...grp, clauses: newClauses }, found: true };
                }
                let found = false;
                const updatedClauses = grp.clauses.map(clause => {
                    if (!found && 'clauses' in clause) {
                        const result = findAndInsertAfter(clause);
                        if (result.found) {
                            found = true;
                            return result.group;
                        }
                    }
                    return clause;
                });
                return { group: { ...grp, clauses: updatedClauses }, found };
            };
            const result = findAndInsertAfter(updatedTree);
            updatedTree = result.group;
            inserted = result.found;
        }
        else if (overId.includes('-between-')) {
            const betweenPart = overId.split('-between-')[1];
            const clauseIds = getAllItemIds(updatedTree.clauses);
            let secondId = '';
            for (const clauseId of clauseIds) {
                if (betweenPart.endsWith('-' + clauseId)) {
                    secondId = clauseId;
                    break;
                }
            }
            if (secondId) {
                updatedTree = insertItemBefore(updatedTree, secondId, item);
                inserted = true;
            }
        }
        else if (overId.endsWith('-root-drop')) {
            inserted = false;
        }
        else if (isGroupById(updatedTree, overId)) {
            updatedTree = addItemToGroup(updatedTree, overId, item);
            inserted = true;
        }
        if (inserted) {
            emitMajorChange(updatedTree);
        }
        else {
            emitChange(tree, false);
        }
    };
    const getAllItemIds = React.useCallback((items) => {
        const ids = [];
        for (const item of items) {
            ids.push(item.id);
            if ('clauses' in item) {
                ids.push(...getAllItemIds(item.clauses));
            }
        }
        return ids;
    }, []);
    const updateGroupById = useCallback((groupId, updates, rootGroup) => {
        if (rootGroup.id === groupId) {
            return { ...rootGroup, ...updates };
        }
        return {
            ...rootGroup,
            clauses: rootGroup.clauses.map(clause => {
                if ('clauses' in clause) {
                    return updateGroupById(groupId, updates, clause);
                }
                return clause;
            })
        };
    }, []);
    const handleGroupUpdate = (updates) => {
        emitMinorChange({ ...tree, ...updates });
    };
    const handleGroupMajorUpdate = (updates) => {
        emitMajorChange({ ...tree, ...updates });
    };
    const handleGroupMinorUpdate = (updates) => {
        emitMinorChange({ ...tree, ...updates });
    };
    const findDraggedItem = React.useCallback((itemId, searchTree) => {
        const findInGroup = (group) => {
            for (const clause of group.clauses) {
                if (clause.id === itemId) {
                    return clause;
                }
                if ('clauses' in clause) {
                    const found = findInGroup(clause);
                    if (found)
                        return found;
                }
            }
            return null;
        };
        return findInGroup(searchTree);
    }, []);
    const clearFilters = () => {
        emitMajorChange({ id: uuidv4(), kind: 'And', clauses: [] });
        requestAnimationFrame(() => {
            addConditionButtonRef.current?.focus();
        });
    };
    const toggleOpen = () => {
        setIsOpen(prev => !prev);
    };
    const flush = useCallback(() => {
        commitPendingMinorChange();
        if (tree.clauses.length === 0) {
            onChange?.({ enabled: false });
        }
        else {
            onChange?.({ enabled: true, config: convertToReportConfig(tree) });
        }
    }, [commitPendingMinorChange, tree, onChange, convertToReportConfig]);
    useImperativeHandle(ref, () => ({
        clearFilters,
        toggleOpen,
        flush,
    }), [clearFilters, flush]);
    const activeFilterCount = countActiveFilters(tree);
    const hasActiveFilters = activeFilterCount > 0 || tree.clauses.length > 0;
    const showReloadingSpinner = useSpinDelay(isReloading, {
        delay: 0,
        minDuration: 800
    });
    return (_jsxs(Popover, { open: isOpen, onOpenChange: (open) => {
            setIsOpen(open);
            onOpenChange?.(open);
        }, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx(Button, { className: cn("transition-colors duration-300 text-left flex-grow rounded-md ring-[1px]", hasActiveFilters
                        ? "ring-[2px] ring-blue-700 bg-blue-50 text-blue-700 hover:bg-blue-100 focus-visible:bg-blue-100"
                        : "ring-gray-300 bg-[#FFFFFF] hover:bg-[var(--ak-color-button-secondary-background-hover)] focus-visible:bg-[var(--ak-color-button-secondary-background-hover)]", "data-[state=open]:ring-2 data-[state=open]:ring-blue-700 data-[state=open]:ring-offset-0"), variant: "unstyled", icon: _jsx(Icon, { name: "FunnelIcon", type: "outline", className: "flex-shrink-0" }), size: "md", children: _jsxs("div", { className: "inline-flex gap-x-2", children: [_jsx("span", { children: L.component_library.filter_panel.filters }), hasActiveFilters && (_jsx("span", { className: "border-l-[1px] border-blue-200 pl-2", children: activeFilterCount }))] }) }) }), _jsxs(PopoverContent, { align: "start", ref: filterPanelRef, className: cn("min-w-64 w-[var(--radix-popper-available-width)] sm:w-full sm:min-w-[480px] md:min-w-[640px] bg-white p-2", className), onEscapeKeyDown: handleEscapeKeyDown, onInteractOutside: (e) => {
                    if (onConfirm) {
                        if (hasUnsavedChanges) {
                            e.preventDefault();
                            setShowInteractionWarning(true);
                            setAlertKey(prev => prev + 1);
                        }
                        else {
                            setIsOpen(false);
                        }
                    }
                    else {
                    }
                }, children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "py-1 px-2", children: hasActiveFilters && (_jsx("span", { id: "filter-dialog-title", className: "text-sm font-medium text-gray-900", children: L.component_library.filter_panel.in_this_view })) }), _jsxs("div", { className: "flex items-center gap-4 justify-end", children: [showReloadingSpinner && (_jsxs("div", { className: "inline-flex items-center gap-2 text-sm text-gray-800", children: [_jsx(Spinner, { size: "xsmall", className: "p-0" }), _jsx("span", { className: "text-xs flex-shrink-0 hidden sm:inline", children: L.component_library.filter_panel.loading_data })] })), _jsxs("div", { className: "flex items-center gap-2", children: [operationsHistory.canUndo && (_jsx(Button, { "aria-label": "Undo", onClick: handleUndo, variant: "secondary", size: "sm", children: L.component_library.filter_panel.undo })), operationsHistory.canRedo && (_jsx(Button, { "aria-label": "Redo", onClick: handleRedo, variant: "secondary", size: "sm", children: L.component_library.filter_panel.redo })), hasActiveFilters && onConfirm && (_jsx(Button, { variant: "secondary", size: "sm", onClick: clearFilters, children: L.component_library.filter_panel.clear_all }))] }), !onConfirm && (_jsx(Button, { icon: _jsx(Icon, { name: "XMarkIcon", role: "decorative", type: "solid" }), "aria-label": "Close", onClick: () => setIsOpen(false), variant: "secondary", size: "sm" }))] })] }), _jsxs("div", { className: "relative", children: [isFlashing && _jsx("div", { className: styles.flashOverlay }), _jsxs(DndContext, { sensors: sensors, collisionDetection: customCollisionDetection, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, onDragCancel: handleDragCancel, modifiers: [restrictToVerticalAxis], children: [_jsx(FilterGroup, { group: tree, availableFields: availableFields, onUpdate: handleGroupUpdate, onMajorUpdate: handleGroupMajorUpdate, onMinorUpdate: handleGroupMinorUpdate, isRoot: true, activeId: activeId, overId: overId, maxDepth: maxDepth }), _jsx(DragOverlay, { dropAnimation: null, modifiers: [restrictToVerticalAxis, adjustOverlayPosition], children: activeId ? (() => {
                                            const draggedItem = findDraggedItem(activeId, tree);
                                            if (!draggedItem)
                                                return null;
                                            if ('field' in draggedItem) {
                                                return (_jsx("div", { className: "bg-white border border-blue-500 rounded shadow-lg p-2 opacity-80", children: _jsx(FilterCondition, { condition: draggedItem, availableFields: availableFields, onUpdate: () => { }, onDelete: undefined }) }));
                                            }
                                            else {
                                                return (_jsx("div", { className: "bg-white border border-blue-500 rounded shadow-lg p-2 opacity-80", children: _jsxs("div", { className: "text-sm font-medium text-blue-700", children: [draggedItem.kind, " ", L.component_library.filter_panel.group, " (", draggedItem.clauses.length, " ", draggedItem.clauses.length === 1 ? 'item' : 'items', ")"] }) }));
                                            }
                                        })() : null })] })] }), !hasActiveFilters && (_jsxs("div", { className: "text-sm text-gray-500 flex flex-col items-center gap-2 py-12 my-4 border-[1px] border-gray-200 rounded", children: [_jsx("span", { className: "text-center text-base", children: L.component_library.filter_panel.no_filters_configured }), _jsx(Button, { variant: "primary", size: "md", onClick: () => handleAddCondition(true), icon: _jsx(Icon, { name: "PlusIcon", type: "solid" }), children: L.component_library.filter_panel.add_condition })] })), _jsx("hr", { className: "mt-0 mb-2" }), showInteractionWarning && hasUnsavedChanges && (_jsx(motion.div, { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.2, ease: "easeOut" }, children: _jsx(Alert, { variant: "danger", mode: "inline", className: "mb-2", children: _jsx("span", { children: L.component_library.filter_panel.confirm_or_cancel }) }) }, alertKey)), _jsxs("div", { className: "flex justify-between space-x-8 items-start", children: [_jsxs("div", { className: "flex flex-col justify-start sm:flex-row gap-2", children: [_jsx(Button, { ref: addConditionButtonRef, variant: "secondary", size: "md", onClick: () => handleAddCondition(false), icon: _jsx(Icon, { name: "PlusIcon", type: "solid" }), children: L.component_library.filter_panel.add_condition }), _jsx(Button, { variant: "secondary", size: "md", onClick: handleAddGroup, icon: _jsx(Icon, { name: "SquaresPlusIcon", type: "solid" }), children: L.component_library.filter_panel.add_group })] }), _jsxs("div", { className: "flex flex-col gap-2 items-center justify-end sm:flex-row ", children: [hasActiveFilters && !onConfirm && (_jsx(Button, { variant: "secondary", size: "md", onClick: clearFilters, children: L.component_library.filter_panel.clear_all })), onConfirm && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", size: "md", onClick: () => {
                                                    const revertedTree = initialConfig.config ? (() => {
                                                        function assignIds(config) {
                                                            const kind = config.logic === 'and' ? 'And' : 'Or';
                                                            return {
                                                                id: uuidv4(),
                                                                kind,
                                                                clauses: config.conditions?.map(clause => 'field' in clause
                                                                    ? ({ id: uuidv4(), ...clause })
                                                                    : assignIds(clause)) || []
                                                            };
                                                        }
                                                        return assignIds(initialConfig.config);
                                                    })() : { id: uuidv4(), kind: 'And', clauses: [] };
                                                    emitChange(revertedTree, false);
                                                    setShowInteractionWarning(false);
                                                    setIsOpen(false);
                                                }, children: L.component_library.filter_panel.cancel }), _jsx(Button, { size: "md", onClick: () => {
                                                    const newConfig = { enabled: true, config: convertToReportConfig(tree) };
                                                    onConfirm(newConfig);
                                                    setInitialConfig(newConfig);
                                                    setShowInteractionWarning(false);
                                                    setIsOpen(false);
                                                }, children: L.component_library.filter_panel.apply })] }))] })] })] })] }));
});
